// Local-first storage. Survives 72h of full offline operation (spec §1.4).
//
// Two flavours of table:
//   - reference data  (synced from server, read-only on the client)
//   - transactional   (created offline, eventually flushed via sync engine)
//
// Every row has a client UUID. The sync engine treats that UUID as the
// idempotency key — same row replayed any number of times yields one server
// row.

import Dexie, { type Table } from 'dexie';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type SyncStatus = 'pending' | 'syncing' | 'synced' | 'conflict' | 'failed';

export interface MenuCategory {
  id: string;
  branch_id: string | null;
  name: string;
  sort_order: number;
  is_active: boolean;
  updated_at: string;
}

export interface MenuItem {
  id: string;
  branch_id: string | null;
  category_id: string | null;
  sku: string;
  name: string;
  base_price: number;       // minor units
  currency: string;
  is_available: boolean;
  kitchen_station: string | null;
  tags: string[];
  image_url?: string | null;
  updated_at: string;
}

export interface LocalOrder {
  local_id?: number;        // auto-increment Dexie key
  uuid: string;             // client-generated, server idempotency key
  branch_id: string;
  tenant_id: string;
  order_type: 'dine_in' | 'takeaway' | 'delivery' | 'online' | 'credit';
  status: 'open' | 'sent_to_kitchen' | 'paid' | 'cancelled';
  table_number: string | null;
  currency: string;
  sub_total: number;
  discount_total: number;
  tax_total: number;
  service_charge: number;
  tip: number;
  grand_total: number;
  placed_at: string;
  placed_by: string | null;
  sync_status: SyncStatus;
  sync_error?: string;
  local_order_number: string;
}

export interface LocalOrderItem {
  local_id?: number;
  order_uuid: string;
  menu_item_id: string;
  name_snapshot: string;
  unit_price: number;
  quantity: number;
  discount: number;
  tax_rate: number;
  line_total: number;
  modifiers: Array<{ name: string; price_delta: number }>;
  notes?: string;
}

export interface LocalPayment {
  local_id?: number;
  uuid: string;
  order_uuid: string;
  method: 'cash' | 'card' | 'momo' | 'voucher' | 'split';
  provider?: string;
  amount: number;
  currency: string;
  tendered?: number;
  change_given?: number;
  reference?: string;
  captured_at: string;
  captured_by: string | null;
  sync_status: SyncStatus;
}

export interface PendingOperation {
  id?: number;
  uuid: string;
  type: 'order.create' | 'payment.create' | 'stock.movement' | 'shift.event';
  occurred_at: string;
  payload: unknown;
  attempt_count: number;
  next_retry_at?: string;
  last_error?: string;
}

export interface LocalConflict {
  id?: number;
  operation_uuid: string;
  entity_type: string;
  code: string;
  details: unknown;
  created_at: string;
}

export interface LocalSetting {
  key: string;
  value: unknown;
  updated_at: string;
}

/** A cart parked by a cashier — recallable later. Server-side it's nothing. */
export interface HeldOrder {
  id?: number;
  label: string;            // e.g. "Table 5 — Adwoa" — cashier-defined
  table_number: string | null;
  cart: Array<{
    menu_item_id: string;
    name: string;
    unit_price: number;
    quantity: number;
    tax_rate: number;
    modifiers: Array<{ name: string; price_delta: number }>;
    notes?: string;
    is_special_order?: boolean;
  }>;
  held_at: string;
  held_by: string | null;
  // Hub context — so resuming a held Credit/Delivery order restores the
  // company/employee/address instead of just the line items.
  order_type?:              string;
  customer_name?:           string | null;
  customer_phone?:          string | null;
  credit_customer_id?:      string | null;
  credit_customer_name?:    string | null;
  credit_employee_id?:      string | null;
  credit_discount_percent?: number;
  delivery_address?:        string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Dexie schema (spec §6.2)
// ─────────────────────────────────────────────────────────────────────────────

class OfiCiaDb extends Dexie {
  menu_categories!: Table<MenuCategory, string>;
  menu_items!:      Table<MenuItem, string>;
  orders!:          Table<LocalOrder, number>;
  order_items!:     Table<LocalOrderItem, number>;
  payments!:        Table<LocalPayment, number>;
  pending_ops!:     Table<PendingOperation, number>;
  conflicts!:       Table<LocalConflict, number>;
  settings!:        Table<LocalSetting, string>;
  held_orders!:     Table<HeldOrder, number>;

  constructor() {
    super('ofi-cia-pos');
    this.version(1).stores({
      menu_categories: 'id, branch_id, sort_order',
      menu_items:      'id, branch_id, category_id, sku, is_available',
      orders:          '++local_id, &uuid, branch_id, sync_status, placed_at',
      order_items:     '++local_id, order_uuid, menu_item_id',
      payments:        '++local_id, &uuid, order_uuid, sync_status',
      pending_ops:     '++id, &uuid, type, next_retry_at',
      conflicts:       '++id, operation_uuid, entity_type, created_at',
      settings:        '&key',
    });
    // Schema v2 adds the held_orders bucket. Existing browsers upgrade in
    // place; held carts from a previous version don't exist so no data loss.
    this.version(2).stores({
      held_orders: '++id, held_at',
    });
  }
}

export const db = new OfiCiaDb();

export async function getSetting<T = unknown>(key: string): Promise<T | undefined> {
  const row = await db.settings.get(key);
  return row?.value as T | undefined;
}

export async function setSetting(key: string, value: unknown): Promise<void> {
  await db.settings.put({ key, value, updated_at: new Date().toISOString() });
}
